import { useState, useEffect } from "react";
import { useLazyQuery } from "@apollo/client";
import { ADMIN_GET_ALL_TRANSFERS } from "../../services/graphql/admin/transfers";

/**
 * Custom hook for fetching and managing the transfers list
 * @returns {object} Hook state and handlers
 */
const useAdminTransferList = () => {
  const [filters, setFilters] = useState({
    sortBy: "createdAt",
    sortOrder: "desc",
    limit: 20,
    skip: 0,
  });

  const [currentPage, setCurrentPage] = useState(1);

  const [getTransfers, { data, loading, error, refetch }] = useLazyQuery(
    ADMIN_GET_ALL_TRANSFERS,
    {
      fetchPolicy: "network-only",
    }
  );

  // Fetch transfers when filters change
  useEffect(() => {
    const variables = {
      filters: {
        ...filters,
        // Remove undefined values
        status: filters.status || undefined,
        orderType: filters.orderType || undefined,
        businessId: filters.businessId || undefined,
        search: filters.search || undefined,
        startDate: filters.startDate || undefined,
        endDate: filters.endDate || undefined,
      },
    };

    getTransfers({ variables });
  }, [filters, getTransfers]);

  const handleFilterChange = (newFilters) => {
    // Reset to page 1 when filters change
    setCurrentPage(1);
    setFilters({
      ...newFilters,
      skip: 0,
      limit: 20,
    });
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    setFilters({
      ...filters,
      skip: (page - 1) * 20,
    });
  };

  const handleClearFilters = () => {
    setCurrentPage(1);
    setFilters({
      sortBy: "createdAt",
      sortOrder: "desc",
      limit: 20,
      skip: 0,
    });
  };

  return {
    transfers: data?.adminGetAllTransfers?.transfers || [],
    totalCount: data?.adminGetAllTransfers?.totalCount || 0,
    hasMore: data?.adminGetAllTransfers?.hasMore || false,
    loading,
    error,
    filters,
    currentPage,
    onFilterChange: handleFilterChange,
    onPageChange: handlePageChange,
    onClearFilters: handleClearFilters,
    refetch,
  };
};

export default useAdminTransferList;
